import {
  SecondaryMenuTrigger,
  DropDownSection,
  NumInputBoxTwoCol,
  ColorPickerSection,
  SecondaryMenu,
  SecondaryMenuSectionTitle,
} from '../../../component';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import { getBlockSidesFromString } from '../../../../GridDndEditor/Block/Helper/BlockSides';
import { DEFAULT_BLOCK_BACKGROUND_COLOR, DEFAULT_BLOCK_BORDER_COLOR } from '../../../../shared/documentColorDefaults';

import {
  EditorDefaultSidePanelType,
  setActiveSidePanel,
  setIsAdvancedSpacingModelOpen,
} from '../../../../grid/reduxStore/blockStyleSettingsSlice';
import { useBlockStyleSettings } from '../../../../hooks/useBlockSettings';
import type { RootState } from '../../../../grid/reduxStore/Store';
import { useAppDispatch } from '../../../../grid/reduxStore/Store';
import { useCircularShape } from './useCircularShape';
import { SelectionContext } from 'components/editor/GridDndEditor/SelectedBlockInfoProvider';
import { useContext } from 'react';
import { useBlockContentWithSectionId } from 'components/editor/Sections/useBlockContent';
import { useSelector } from 'react-redux';
import type { BlockContent } from 'components/editor/grid/reduxStore/editorSlice';
import { selectSectionIdByBlockId } from 'components/editor/grid/reduxStore/editorSlice';

export function TextBlockSettings() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    changeBlockBackgroundColor,
    getBlockStyle,
    changeBlockBorderColor,
    changeBlockBorderSide,
    changeBorderRadius,
    changeBorderWidth,
    saveCurrentStateToRef,
  } = useBlockStyleSettings();
  const {
    backgroundColor = DEFAULT_BLOCK_BACKGROUND_COLOR,
    borderColor = DEFAULT_BLOCK_BORDER_COLOR,
    borderRadius = 0,
    currentBorderSide = [],
    borderWidth = 0,
  } = getBlockStyle();

  const { selectedBlockIdByWrapper } = useContext(SelectionContext);
  const blockId = selectedBlockIdByWrapper as string;
  const sectionId = useSelector((state: RootState) => selectSectionIdByBlockId(state, blockId));
  const blockContent = useBlockContentWithSectionId(sectionId ?? '', blockId) as BlockContent;
  const { wouldBeCircular } = useCircularShape(blockContent);

  const handleBackButtonClick = () => {
    dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL }));
  };

  const handleBorderRadiusChange = (radius: number) => {
    const isCircularShape = radius > 0 && wouldBeCircular(radius);
    changeBorderRadius({ radius, toAddPadding: !isCircularShape });
  };

  return (
    <SecondaryMenu
      testId={'textbox-settings-component'}
      menuTitle={t('document.grid.text.design.title')}
      backButtonOnClick={handleBackButtonClick}
    >
      <Stack rowGap="16px" width="100%">
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.text.design.background')}</SecondaryMenuSectionTitle>
        <ColorPickerSection
          currentColor={backgroundColor}
          onColorChange={(color) => changeBlockBackgroundColor({ color, canBeUndo: false })}
          onOpen={() => saveCurrentStateToRef(getBlockStyle())}
          onClose={(color) => changeBlockBackgroundColor({ color })}
        />
      </Stack>
      <Stack rowGap="16px" width="100%">
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.text.design.border')}</SecondaryMenuSectionTitle>
        <ColorPickerSection
          currentColor={borderColor}
          onColorChange={(color) => changeBlockBorderColor({ color, canBeUndo: false })}
          onOpen={() => saveCurrentStateToRef(getBlockStyle())}
          onClose={(color) => changeBlockBorderColor({ color })}
        />
        <DropDownSection
          currentSelection={currentBorderSide}
          handleSelectionChange={(borderSideStr) => {
            changeBlockBorderSide(getBlockSidesFromString(borderSideStr));
          }}
          testId={'border-dropdown'}
        />
        <NumInputBoxTwoCol
          inputValue={borderWidth}
          onChange={changeBorderWidth}
          title={t('document.grid.text.design.border_width')}
          testId={'border-width'}
          disableNegativeNum
        />
        <NumInputBoxTwoCol
          inputValue={borderRadius}
          onChange={handleBorderRadiusChange}
          title={t('document.grid.text.design.border_radius')}
          testId={'border-radius'}
          disableNegativeNum
        />
      </Stack>
      <SecondaryMenuTrigger
        ButtonText={t('document.grid.text.advanced_spacing.title')}
        ButtonDesc={t('document.grid.text.advanced_spacing.adjust_left_top_right_bottom_block_padding')}
        handleClick={() => {
          dispatch(setIsAdvancedSpacingModelOpen(true));
        }}
      />
    </SecondaryMenu>
  );
}
